import i18next from 'i18next'
import { Map } from 'immutable'
import * as React from 'react'
import { ComponentDidMount, fromJSX, mk_pair, Widget } from 'widgets-for-react'
import { Updater } from "widgets-for-react"
import { dataLayerServices } from '../seo/datalayer'
import { cookiebarClasses, CookiebarLayout, cookiePlaceholderClasses } from './cookiebar.layout'
import { CookieAccepted, CookiebarState, CookieName, CookieState } from './cookiebar.state'
import { getCookie, setConsentCookie, setCookie, updateConsent } from './cookiebar.utils'
import { grantAllConsentTypes, grantMinimalConsentTypes } from '../seo/datalayer.consent'

const translate = (x: string) => i18next.t(x)

const _CookiebarLayout = CookiebarLayout()

export const cookiebarStateZero = (props: {
  cookieNames: CookieName[]
  labels: Map<CookieName, string>
  htmlFor: Map<CookieName, string>
  readonlyCookies?: CookieName[]
  requiredCookies?: CookieName[]
}): CookiebarState => {
  let Cookies: Map<CookieName, CookieState> = Map<CookieName, CookieState>()
  let isIn = (c: CookieName, cList: CookieName[]) => {
    if (cList === undefined) return false
    return cList.find((c1) => c === c1) !== undefined
  }
  let isIn2 = (c: CookieName, cList: Map<CookieName, string>) => {
    if (cList === undefined) return ''
    if (cList.has(c)) return cList.get(c)
    return ''
  }
  props.cookieNames.forEach((cookieName) => {
    let isAccepted: CookieAccepted =
      getCookie(cookieName) === 'yes' || isIn(cookieName, props.readonlyCookies!) ? 'yes' : 'no'
    if (isAccepted) updateConsent(cookieName, isAccepted == 'yes')
    Cookies = Cookies.set(cookieName, {
      isAccepted,
      isDisabled: isIn(cookieName, props.readonlyCookies!),
      isRequired: isIn(cookieName, props.requiredCookies!),
      label: isIn2(cookieName, props.labels)!,
      htmlFor: isIn2(cookieName, props.htmlFor)!
    })
  })

  return {
    Cookies,
    tmpCookies: Cookies.toMap(),
    ExpandOptions: false,
    CookieBarVisible: shouldShowCookies()
  }
}

export const makeEmptyCookieStateZero = () =>
  cookiebarStateZero({
    cookieNames: [],
    htmlFor: Map(),
    labels: Map(),
    readonlyCookies: [],
    requiredCookies: []
  })

export const makeProjectCookieStateZero = () =>
  cookiebarStateZero({
    cookieNames: ['functional-cookies', 'analytical-cookies', 'video-cookies', 'tracking-cookies'],
    htmlFor: Map()
      .set('functional-cookies', 'functional-cookies')
      .set('analytical-cookies', 'analytical-cookies')
      .set('video-cookies', 'video-cookies')
      .set('tracking-cookies', 'tracking-cookies') as Map<CookieName, string>,
    labels: Map()
      .set('functional-cookies', i18next.t('CookieBar:Functional') + '. ' + i18next.t('CookieBar:FunctionalCookies'))
      .set('analytical-cookies', i18next.t('CookieBar:Analytical') + '. ' + i18next.t('CookieBar:AnalyticalCookies'))
      .set('video-cookies', i18next.t('CookieBar:Videos') + '. ' + i18next.t('CookieBar:VideosCookies'))
      .set('tracking-cookies', i18next.t('CookieBar:Tracking') + '. ' + i18next.t('CookieBar:TrackingCookies')) as Map<
      CookieName,
      string
    >,
    readonlyCookies: ['functional-cookies', 'analytical-cookies'],
    requiredCookies: ['functional-cookies', 'analytical-cookies']
  })

export const shouldShowCookies = () => {
  return getCookie('showCookies') !== 'no'
}

export const areCookiesDifferent = (c1: CookiebarState, c2: CookiebarState) => {
  let answer = true
  c1.Cookies.forEach((v: any, n: any) => {
    if (c2.Cookies.get(n)?.isAccepted !== v.isAccepted) {
      answer = false
    }
  })
  return !answer
}

export const isCookieAccepted = (cookieName: CookieName): boolean => {
  return getCookie(cookieName) === 'yes'
}

export const cookiebarWidget = (
  state: CookiebarState,
  showSingleCookie: CookieName = undefined as any,
  placeholderText?: string | JSX.Element,
  placeholderButtonLabel?: string
): Widget<Updater<CookiebarState>> => {
  return fromJSX<Updater<CookiebarState>>(
    (setState) => {
      let applyTmpCookies = () =>
        setState((state) => {
          state.tmpCookies.forEach((v: any, n: any) => {
            setConsentCookie(n, v.isAccepted)
          })
          setCookie('showCookies', 'no', 1000)
          let shouldReloadPage = false
          state.tmpCookies.forEach((v: any, k: any) => {
            if (state.Cookies.has(k) && state.Cookies.get(k)?.isAccepted === 'yes' && v.isAccepted === 'no') {
              shouldReloadPage = true
            }
          })
          if (shouldReloadPage) {
            location.reload()
            return { ...state, CookieBarVisible: false }
          }
          return {
            ...state,
            Cookies: state.tmpCookies.toMap(),
            CookieBarVisible: false
          }
        })

      let apply = (cookieAccepted: CookieAccepted) => () =>
        setState((state) => {
          setCookie('showCookies', 'no', 1000)
          
          if (cookieAccepted == 'yes') {
            grantAllConsentTypes()
            dataLayerServices.cookiesDatalayer(true)
          } else {
            grantMinimalConsentTypes()
            dataLayerServices.cookiesDatalayer(false)
          }

          let updatedCookies: Map<CookieName, CookieState> = state.Cookies.map((cValue: any, cName: any) => {
            setConsentCookie(cName, cookieAccepted)
            return { ...cValue, isAccepted: cookieAccepted }
          }).toMap()
          if (cookieAccepted === 'no') {
            location.reload()
            return { ...state, CookieBarVisible: false }
          }
          return {
            ...state,
            Cookies: updatedCookies,
            CookieBarVisible: false
          }
        })

      let applySingle = (cName: CookieName, cValue: CookieAccepted, useTmp: boolean) =>
        setState((state) => {

          if (useTmp) {
            let cookie = state.tmpCookies.get(cName)
            if (cookie === undefined) console.error('missing cookie in state', cName)
            let updatedCookies: Map<CookieName, CookieState> = state.tmpCookies.set(cName, {
              ...cookie!,
              isAccepted: cValue
            })
            return {
              ...state,
              tmpCookies: updatedCookies
            }
          } else {
            setConsentCookie(cName, cValue)
            let cookie = state.Cookies.get(cName)
            if (cookie === undefined) console.error('missing cookie in state', cName)
            let updatedCookies: Map<CookieName, CookieState> = state.Cookies.set(cName, {
              ...cookie!,
              isAccepted: cValue
            })
            return {
              ...state,
              Cookies: updatedCookies,
              tmpCookies: updatedCookies
            }
          }
        })

      if (showSingleCookie !== undefined) {
        return (
          <_CookiebarLayout.CookiePlaceholderLayout
            text={placeholderText || ''}
            onAccept={() => applySingle(showSingleCookie, 'yes', false)}
            acceptLabel={placeholderButtonLabel || ''}
            options={{ style: cookiePlaceholderClasses }}
            // SVGIcon={{ width: '10px', height: '10px' }}
          />
        )
      }
      if (!state.CookieBarVisible) return <></>
      return (
        <ComponentDidMount onMount={() => {}}>
          <_CookiebarLayout.CookiebarLayout
            onAcceptAll={apply('yes')}
            acceptAllLabel={translate('cookies:cookiebarAcceptCookies')}
            declineAllLabel={translate('cookies:cookiebarDeclineCookies')}
            onDeclineAll={apply('no')}
            onApplySettings={applyTmpCookies}
            applySettingsLabel={translate('cookies:cookiebarChangeSettings')}
            onSetSingle={(name, value) => applySingle(name, value, true)}
            showOptions={state.ExpandOptions}
            showOptionsLabel={translate('cookies:cookiebarShowOptions')}
            toggleOptions={() => setState((s) => ({ ...s, ExpandOptions: !s.ExpandOptions }))}
            title={translate('cookies:cookiebarTitle')}
            intro={translate('cookies:cookiebarIntro')}
            introSettings={translate('cookies:cookiebarIntroSettings')}
            state={state.tmpCookies
              .map((cv: any, cn: any) => mk_pair(cn, cv))
              .valueSeq()
              .toList()}
            options={{ style: cookiebarClasses }}
            // SVGIcon={{ width: '10px', height: '10px' }}
            // ExpandSVGIcon={{ width: '10px', height: '7px' }}
            // SubmitSVGIcon={{ width: '10px', height: '10px' }}
          />
        </ComponentDidMount>
      )
    }
  )
}
